import { Link } from "react-router-dom";
import { useCachedGet } from "@/lib/cache";
import { hiringLabel } from "@/components/app/clients/ClientDialog";

const FIELD = "w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none";
const LABEL = "text-xs text-slate-400 uppercase tracking-wider";

export const jobClientDefaults = (job = {}) => ({
  client_id: job.client_id || "", client_job_id: job.client_job_id || "",
  account_id: job.account_id || "", service_line_id: job.service_line_id || "", job_type: job.job_type || "",
});

export const jobClientPayload = (v) => ({
  client_id: v.client_id, client_job_id: (v.client_job_id || "").trim(),
  account_id: v.account_id || null, service_line_id: v.service_line_id || null, job_type: v.job_type || null,
});

// Client → Account / Service Line / Job Type cascade shared by New and Edit Job.
export default function JobClientFields({ value, onChange, prefix = "job" }) {
  const [clients] = useCachedGet("/clients");
  const list = clients || [];
  const client = list.find(c => c.id === value.client_id);
  const set = (patch) => onChange({ ...value, ...patch });
  const pickClient = (id) => {
    const c = list.find(x => x.id === id);
    set({ client_id: id, account_id: "", service_line_id: "", job_type: c?.hiring_types?.length === 1 ? c.hiring_types[0] : "" });
  };
  if (clients !== undefined && list.length === 0) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid={`${prefix}-no-clients`}>
        No clients available. <Link to="/app/clients" className="font-medium underline">Onboard a client</Link> first, or ask your Tenant Admin to assign you one.
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid={`${prefix}-client-fields`}>
      <div>
        <label className={LABEL}>Client</label>
        <select data-testid={`${prefix}-client-id`} required value={value.client_id} onChange={(e) => pickClient(e.target.value)} className={`tp-select ${FIELD}`}>
          <option value="">Select client</option>
          {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL}>Client Job ID</label>
        <input data-testid={`${prefix}-client-job-id`} value={value.client_job_id} onChange={(e) => set({ client_job_id: e.target.value })} placeholder="e.g. TechM-PIPE-123456" className={FIELD} />
      </div>
      <div>
        <label className={LABEL}>Account</label>
        <select data-testid={`${prefix}-account-id`} value={value.account_id} onChange={(e) => set({ account_id: e.target.value })} disabled={!client} className={`tp-select ${FIELD} disabled:opacity-60`}>
          <option value="">{client ? (client.accounts.length ? "Select account" : "No accounts for this client") : "Select a client first"}</option>
          {(client?.accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}{a.location ? ` — ${a.location}` : ""}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL}>Service Line</label>
        <select data-testid={`${prefix}-service-line-id`} value={value.service_line_id} onChange={(e) => set({ service_line_id: e.target.value })} disabled={!client} className={`tp-select ${FIELD} disabled:opacity-60`}>
          <option value="">{client ? (client.service_lines.length ? "Select service line" : "No service lines for this client") : "Select a client first"}</option>
          {(client?.service_lines || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={LABEL}>Job Type</label>
        <div className="flex gap-2 mt-1">
          {(client?.hiring_types || ["fte", "contract"]).map(t => {
            const on = value.job_type === t;
            return (
              <button type="button" key={t} data-testid={`${prefix}-job-type-${t}`} aria-pressed={on} disabled={!client} onClick={() => set({ job_type: t })}
                className={`px-3.5 py-2 text-sm rounded-lg border transition-colors disabled:opacity-50 ${on ? "bg-indigo-600 border-indigo-600 text-white" : "border-[color:var(--bd-sub)] text-slate-600 hover:border-indigo-400"}`}>
                {hiringLabel(t)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
